import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings, QrCode, Download } from "lucide-react";
import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import ResetClubData from "@/components/admin/ResetClubData";


const FIELDS = [
  ["name", "club.name"], ["address", "club.address"], ["opening_hours", "club.hours"], ["court_price", "club.price", "number"],
  ["price_per_slot", "club.price_player", "number"], ["juice_number", "club.juice"], ["phone", "club.phone"], ["whatsapp", "club.whatsapp"],
  ["logo_url", "club.logo"], ["maps_url", "club.maps"],
];

export default function AdminCourtSettings() {
  const { t } = useLanguage();
  const [club, setClub] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/club").then(r => setClub(r.data)).catch(() => {}); }, []);
  const set = (k, type) => (e) => setClub(c => ({ ...c, [k]: type === "number" ? e.target.value : e.target.value }));

  const save = async () => {
    setSaving(true);
    const payload = {
      name: club.name, description: club.description || "", address: club.address || "", region: club.region || "Tamarin", images: club.images || [], amenities: club.amenities || [],
      opening_hours: club.opening_hours, price_per_slot: parseFloat(club.price_per_slot) || 0, court_price: parseFloat(club.court_price) || 0,
      juice_number: club.juice_number || "", phone: club.phone || "", whatsapp: club.whatsapp || "", logo_url: club.logo_url || "", maps_url: club.maps_url || "",
    };
    try { const r = await api.put(`/clubs/${club._id}`, payload); setClub(c => ({ ...c, ...r.data })); toast.success(t("club.saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!club) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <Card className="border-border/50 lg:col-span-2" data-testid="admin-court-settings">
      <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><Settings size={18} className="text-coral" /> {t("club.title")}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label, type]) => (
            <div key={k}><Label>{t(label)}</Label><Input type={type || "text"} value={club[k] ?? ""} onChange={set(k, type)} className="rounded-xl" data-testid={`settings-${k}`} /></div>
          ))}
          <div className="sm:col-span-2"><Label>{t("club.cover")}</Label><Input value={club.images?.[0] || ""} onChange={e => setClub(c => ({ ...c, images: e.target.value ? [e.target.value] : [] }))} className="rounded-xl" data-testid="settings-cover-image" /></div>
          <div className="sm:col-span-2"><Label>{t("club.description")}</Label><Textarea value={club.description || ""} onChange={set("description")} className="rounded-xl min-h-[80px]" data-testid="settings-description" /></div>
        </div>
        <p className="text-xs text-muted-foreground">{t("club.hint")}</p>
        <Button onClick={save} disabled={saving} className="rounded-full bg-coral hover:bg-coral-600 text-white px-8" data-testid="save-settings-btn">{saving ? t("club.saving") : t("club.save")}</Button>
      </CardContent>
    </Card>
    <QrCard />
      <ResetClubData />
    </div>
  );
}

function QrCard() {
  const { t } = useLanguage();
  const qrRef = useRef(null);
  const siteUrl = `${window.location.origin}/welcome`;
  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "dd-padel-qr.png";
    a.click();
  };
  return (
    <Card className="border-border/50" data-testid="qr-card">
      <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><QrCode size={18} className="text-coral" /> {t("club.qr")}</CardTitle></CardHeader>
      <CardContent className="flex flex-col items-center gap-3">
        <div ref={qrRef} className="p-3 bg-white rounded-2xl"><QRCodeCanvas value={siteUrl} size={180} level="M" includeMargin={false} /></div>
        <p className="text-[11px] text-muted-foreground text-center break-all">{siteUrl}</p>
        <p className="text-xs text-muted-foreground text-center">{t("club.qr_hint")}</p>
        <Button onClick={downloadQr} variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="download-qr-btn"><Download size={13} /> {t("club.download")}</Button>
      </CardContent>
    </Card>
  );
}
